import React, { useState, useCallback, useEffect } from 'react';
import { toast as message } from 'sonner';
import type { ICellRendererParams, IHeaderParams, GridApi } from 'ag-grid-community';

import './ActionCell.css';
import RecordLink from './links/RecordLink';
import { useDeleteById } from '../utils/metadata';
import type { BaseRecord } from '../types/api';

interface ActionCellRendererProps extends ICellRendererParams {
  context: {
    tableName: string;
    schema: string;
    removeClientSideRow: (id: number) => void;
    isClientSideRow: (data: BaseRecord) => boolean;
  };
  showDelete?: boolean;
  showViewRecord?: boolean;
  routeName?: string;
}

interface ActionHeaderProps extends IHeaderParams {
  gridConfig: any;
  handleNewRow: () => void;
  showAdd?: boolean;
  showClearFilters?: boolean;
  routeName?: string;
}

// Inline SVG icon components
const IconCross = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);
const IconTrash = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconFilter = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);
const IconPlus = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export const ActionCellRenderer: React.FC<ActionCellRendererProps> = (props) => {
  const { tableName, removeClientSideRow, isClientSideRow } = props.context;
  const id = props?.data?.id;
  const { showDelete, showViewRecord, routeName } = props;
  const [, setIsHovered] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);

  const handleMouseEnter = (): void => setIsHovered(true);
  const handleMouseLeave = (): void => setIsHovered(false);

  const actions: React.ReactNode[] = [];

  const onDeleteFailure = useCallback((error: Error) => {
    message.error(`Failed to delete record: ${error.message}`);
    setShowLoading(false);
  }, []);

  const onDeleteSuccess = useCallback(() => {
    if (props.api) {
      props.api.applyServerSideTransaction({ remove: [{ id }] });
    }
    setShowLoading(false);
  }, [props.api, id]);

  const deleteRow = useDeleteById(tableName, onDeleteSuccess, onDeleteFailure);

  const handleDelete = useCallback(async (): Promise<void> => {
    setShowLoading(true);
    try {
      await deleteRow({ id });
    } catch (error: any) {
      onDeleteFailure(error);
    }
  }, [deleteRow, id, onDeleteFailure]);

  if (isClientSideRow(props.data)) {
    return (
      <button className="sky-icon-btn" onClick={() => removeClientSideRow(props.data.id)} title="Cancel">
        <IconCross />
      </button>
    );
  }

  if (showDelete) {
    actions.push(
      <button className="sky-icon-btn" onClick={handleDelete} title="Delete" key="delete">
        <IconTrash />
      </button>
    );
  }

  if (showViewRecord && routeName) {
    actions.push(
      <div key="view" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <RecordLink tableName={tableName} recordId={props.data.id} title="Open record" />
      </div>
    );
  }

  if (showLoading) {
    actions.push(
      <span className="sky-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} key="loading" />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
      {actions}
    </div>
  );
};

export const ActionHeader: React.FC<ActionHeaderProps> = (props) => {
  const { handleNewRow, showAdd, showClearFilters = true } = props;
  const gridApi: GridApi | undefined = props.api;
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);

  const checkActiveFilters = useCallback(() => {
    if (gridApi) {
      const filterModel = gridApi.getFilterModel();
      setHasActiveFilters(filterModel && Object.keys(filterModel).length > 0);
    }
  }, [gridApi]);

  useEffect(() => {
    if (gridApi) {
      checkActiveFilters();
      const onFilterChanged = (): void => checkActiveFilters();
      gridApi.addEventListener('filterChanged', onFilterChanged);
      return () => gridApi.removeEventListener('filterChanged', onFilterChanged);
    }
  }, [gridApi, checkActiveFilters]);

  const handleClearAllFilters = useCallback(() => {
    if (gridApi && hasActiveFilters) {
      setHasActiveFilters(false);
      gridApi.setFilterModel(null);
    }
  }, [gridApi, hasActiveFilters]);

  const actions: React.ReactNode[] = [];

  if (showClearFilters) {
    actions.push(
      <button
        className={`sky-icon-btn ${hasActiveFilters ? 'clear-filters-active' : 'clear-filters-disabled'}`}
        onClick={handleClearAllFilters}
        disabled={!hasActiveFilters}
        title={hasActiveFilters ? 'Clear All Filters' : 'No active filters'}
        key="clear-filters"
        style={{ marginRight: 4 }}
      >
        <IconFilter />
      </button>
    );
  }

  if (showAdd) {
    actions.push(
      <button className="sky-icon-btn" onClick={handleNewRow} title="Add Row" key="add-row">
        <IconPlus />
      </button>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span>{props.displayName}</span>
      <div style={{ display: 'flex', alignItems: 'center' }}>{actions}</div>
    </div>
  );
};
